/**
 * Helper that opens the Data Import Wizard workspace tab. Content is optional
 * at open time — the wizard can open empty (e.g. Object Explorer "Import
 * Data…" with no file chosen yet) or pre-seeded with a source file and/or a
 * target table (e.g. "Import Data into this table…" from a table's context
 * menu). Mirrors `cross-engine-migrate/openMigrateTab.ts`.
 */
import { useWorkspaceStore } from '../core/store/workspaceStore';
import type { DataImportTabContent, TabConnectionInfo } from '../core/types/workspaceTypes';
// Data Import Wizard contracts — imported from the ambient `electron.d.ts` re-export,
// NOT the electron/ path directly (see the re-export comment there for why).
import type { ImportTargetPlan } from '../../../types/electron';

export type OpenDataImportTabArgs = {
  title?: string;
  filePath?: string;
  target?: ImportTargetPlan;
  connectionInfo?: TabConnectionInfo;
};

/** Open a Data Import Wizard tab, optionally seeded with a source file and/or target. */
export function openDataImportTab(args: OpenDataImportTabArgs = {}): string {
  // Entry points that carry a connection but no full target (db context menu, toolbar,
  // command palette) prefill the Target step's DRAFT with that connection + database —
  // the user shouldn't have to re-pick the database they right-clicked.
  const seedDraft =
    !args.target && args.connectionInfo?.connectionId
      ? {
          connectionId: args.connectionInfo.connectionId,
          ...(args.connectionInfo.database ? { database: args.connectionInfo.database } : {}),
        }
      : undefined;
  const content: Partial<DataImportTabContent> = {
    step: 'source',
    ...(args.filePath ? { filePath: args.filePath } : {}),
    ...(args.target ? { target: args.target } : {}),
    ...(seedDraft ? { draftTarget: seedDraft } : {}),
  };
  return useWorkspaceStore.getState().addTab({
    type: 'data-import',
    title: args.title ?? 'Import Data',
    ...(args.connectionInfo ? { connectionInfo: args.connectionInfo } : {}),
    content,
  });
}
